import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ConnectionMode,
  MarkerType,
  type Node,
  type Edge,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Content } from '@carbon/react';
import {
  Button,
  Tag,
  InlineLoading,
  InlineNotification,
  ActionableNotification,
  IconButton,
  Modal,
} from '@carbon/react';
import {
  Renew,
  ChevronLeft,
  ChartNetwork,
  DecisionTree,
  CircleDash,
  Grid as GridIcon,
  Play,
  Catalog,
  DataBase,
  FlowData,
  Search,
  ChartCustom,
} from '@carbon/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { explorationApi } from '@/lib/api/exploration.api';
import { ExplorerConceptNode } from '@/components/knowledge-explorer/ExplorerConceptNode';
import { ExplorerLegend } from '@/components/knowledge-explorer/ExplorerLegend';
import { applySavedLayout, collectLayout, type SavedLayout } from '@/components/knowledge-explorer/applySavedLayout';
import { FacetSearchPanel } from '@/components/knowledge-search/FacetSearchPanel';
import { ExplorationStatus } from '@/components/knowledge-explorer/ExplorationStatus';
import { ConceptInstanceNode } from '@/components/knowledge-explorer/ConceptInstanceNode';
import { SourceNode } from '@/components/knowledge-explorer/SourceNode';
import { FloatingEdge } from '@/components/graph/FloatingEdge';
import { EvidencePanel } from '@/components/knowledge-explorer/EvidencePanel';
import { GraphQualityPanel } from '@/components/graph-kpi/GraphQualityPanel';
import type { LayoutAlgorithm } from '@/components/graph/LayoutControls';
import { outcomeMessage, useResilientMutation } from '@/lib/query/useResilientMutation';
import { verifyPopulateStarted, type RepopulateVars } from '@/lib/query/verifiers';
import type {
  ExplorationGraph,
  ExplorationGraphNode,
  ExplorationGraphEdge,
  ExplorationViewMode,
} from '@snxt/snxt-onto-share';
import './KnowledgeExplorerPage.scss';


// ── Node / edge type registration ─────────────────────────────────────────────
const nodeTypes = {
  explorerConcept: ExplorerConceptNode,
  conceptInstance: ConceptInstanceNode,
  sourceNode: SourceNode,
};
const edgeTypes = { floating: FloatingEdge };

// ── View mode tabs ─────────────────────────────────────────────────────────────
/**
 * `quality` is a TAB of this page, not a graph view mode: it renders the tier-1 KPI panel instead of
 * the canvas, and it is not an `ExplorationViewMode` because the backend graph endpoint knows nothing
 * about it. Keeping it in the same switcher is what makes it a tab of the exploration rather than a
 * new top-level menu entry — quality belongs to the exploration being studied.
 */
export type ExplorerTab = ExplorationViewMode | 'quality';

export const VIEW_MODES: Array<{
  value: ExplorerTab;
  label: string;
  icon: typeof Catalog;
  tooltip: string;
}> = [
  { value: 'ontology', label: 'Ontology', icon: Catalog, tooltip: 'T-Box concepts + extracted instances' },
  { value: 'kb', label: 'KB', icon: DataBase, tooltip: 'KB → KO → KU → instance → concept' },
  { value: 'concept', label: 'Concept', icon: FlowData, tooltip: 'Instance-centric: instances + evidence KUs' },
  {
    value: 'quality',
    label: 'Quality',
    icon: ChartCustom,
    tooltip: 'Coverage and chain integrity of this exploration, with the offending rows',
  },
];

/** The graph endpoint only knows the three canvas modes. */
export function isGraphMode(tab: ExplorerTab): tab is ExplorationViewMode {
  return tab !== 'quality';
}

// ── Layout algorithm options ───────────────────────────────────────────────────
const layoutOptions: Array<{
  value: LayoutAlgorithm;
  label: string;
  icon: typeof ChartNetwork;
  tooltip: string;
}> = [
  { value: 'force', label: 'Force', icon: ChartNetwork, tooltip: 'Force-directed layout (organic clusters)' },
  { value: 'hierarchical', label: 'Tree', icon: DecisionTree, tooltip: 'Hierarchical layout (top-down tree)' },
  { value: 'circular', label: 'Circle', icon: CircleDash, tooltip: 'Circular layout (all nodes on a ring)' },
  { value: 'grid', label: 'Grid', icon: GridIcon, tooltip: 'Grid layout (evenly spaced columns)' },
];

// ── Force-directed layout (Fruchterman-Reingold inspired, same as editor) ─────

interface PositionMap {
  [id: string]: { x: number; y: number };
}

// ── All layout algorithms ──────────────────────────────────────────────────────

function applyCircularLayout(nodes: Node[]): Node[] {
  if (nodes.length === 0) return nodes;
  const count = nodes.length;
  const radius = Math.max(300, count * 40);
  const angleStep = (2 * Math.PI) / count;
  const centerX = 500;
  const centerY = 400;
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: centerX + radius * Math.cos(index * angleStep - Math.PI / 2),
      y: centerY + radius * Math.sin(index * angleStep - Math.PI / 2),
    },
  }));
}

function applyGridLayout(nodes: Node[]): Node[] {
  if (nodes.length === 0) return nodes;
  const cols = Math.ceil(Math.sqrt(nodes.length));
  const spacingX = 300;
  const spacingY = 200;
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: (index % cols) * spacingX + 100,
      y: Math.floor(index / cols) * spacingY + 100,
    },
  }));
}

function applyHierarchicalLayout(nodes: Node[], edges: Edge[]): Node[] {
  if (nodes.length === 0) return nodes;

  const incomingCount = new Map<string, number>();
  const adjacency = new Map<string, string[]>();

  nodes.forEach((n) => {
    incomingCount.set(n.id, 0);
    adjacency.set(n.id, []);
  });

  edges.forEach((e) => {
    incomingCount.set(e.target, (incomingCount.get(e.target) || 0) + 1);
    const children = adjacency.get(e.source) || [];
    children.push(e.target);
    adjacency.set(e.source, children);
  });

  const layers = new Map<string, number>();
  const roots = nodes.filter((n) => (incomingCount.get(n.id) || 0) === 0);
  if (roots.length === 0) roots.push(nodes[0]);

  const queue: Array<{ id: string; layer: number }> = roots.map((r) => ({
    id: r.id,
    layer: 0,
  }));
  const visited = new Set<string>();

  while (queue.length > 0) {
    const { id, layer } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    layers.set(id, layer);
    (adjacency.get(id) || []).forEach((childId) => {
      if (!visited.has(childId)) queue.push({ id: childId, layer: layer + 1 });
    });
  }

  nodes.forEach((n) => {
    if (!visited.has(n.id)) layers.set(n.id, 0);
  });

  const layerGroups = new Map<number, string[]>();
  layers.forEach((layer, id) => {
    const group = layerGroups.get(layer) || [];
    group.push(id);
    layerGroups.set(layer, group);
  });

  const spacingY = 250;
  const spacingX = 300;
  const positions = new Map<string, { x: number; y: number }>();

  layerGroups.forEach((nodeIds, layer) => {
    const totalWidth = (nodeIds.length - 1) * spacingX;
    const startX = 500 - totalWidth / 2;
    nodeIds.forEach((nodeId, idx) => {
      positions.set(nodeId, {
        x: startX + idx * spacingX,
        y: layer * spacingY + 100,
      });
    });
  });

  return nodes.map((n) => ({
    ...n,
    position: positions.get(n.id) ?? n.position,
  }));
}

function applyForceLayout(
  nodes: Node[],
  edges: Edge[],
  spacing = 50,
): Node[] {
  const nodeCount = nodes.length;
  if (nodeCount === 0) return nodes;

  const NODE_WIDTH = 240;
  const NODE_HEIGHT = 80;
  const NODE_PADDING = Math.round(10 + (spacing / 100) * 110);
  const spacingFactor = 0.6 + (spacing / 100) * 1.0;
  const k =
    (Math.sqrt(
      (NODE_WIDTH + NODE_PADDING) *
        (NODE_HEIGHT + NODE_PADDING) *
        nodeCount,
    ) /
      Math.sqrt(nodeCount)) *
    spacingFactor;
  const IDEAL_EDGE_LENGTH = Math.max(k, NODE_WIDTH + NODE_PADDING + 40);
  const ITERATIONS = 400;
  const INITIAL_TEMPERATURE = IDEAL_EDGE_LENGTH * 2;
  const COOLING_FACTOR = 0.95;
  const GRAVITY = 0.08;

  const edgePairs = edges.map((e) => ({ source: e.source, target: e.target }));

  const degree = new Map<string, number>();
  nodes.forEach((n) => degree.set(n.id, 0));
  edgePairs.forEach(({ source, target }) => {
    degree.set(source, (degree.get(source) || 0) + 1);
    degree.set(target, (degree.get(target) || 0) + 1);
  });

  const positions: PositionMap = {};
  const forces: PositionMap = {};

  // Spiral initial placement (sorted by degree so hub nodes start near centre)
  const sortedByDegree = [...nodes].sort(
    (a, b) => (degree.get(b.id) || 0) - (degree.get(a.id) || 0),
  );
  const spiralSpacing = IDEAL_EDGE_LENGTH * 0.8;
  sortedByDegree.forEach((node, idx) => {
    if (idx === 0) {
      positions[node.id] = { x: 0, y: 0 };
    } else {
      const angle = idx * 2.4;
      const radius = spiralSpacing * Math.sqrt(idx);
      positions[node.id] = {
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      };
    }
    forces[node.id] = { x: 0, y: 0 };
  });

  let temperature = INITIAL_TEMPERATURE;

  for (let iter = 0; iter < ITERATIONS; iter++) {
    nodes.forEach((n) => {
      forces[n.id] = { x: 0, y: 0 };
    });

    // Repulsive forces
    for (let i = 0; i < nodeCount; i++) {
      for (let j = i + 1; j < nodeCount; j++) {
        const idA = nodes[i].id;
        const idB = nodes[j].id;
        const posA = positions[idA];
        const posB = positions[idB];
        const dx = posA.x - posB.x;
        const dy = posA.y - posB.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
        const repForce = (k * k) / dist;
        const fx = (dx / dist) * repForce;
        const fy = (dy / dist) * repForce;
        forces[idA].x += fx;
        forces[idA].y += fy;
        forces[idB].x -= fx;
        forces[idB].y -= fy;
      }
    }

    // Attractive forces (edges)
    edgePairs.forEach(({ source, target }) => {
      const posS = positions[source];
      const posT = positions[target];
      if (!posS || !posT) return;
      const dx = posT.x - posS.x;
      const dy = posT.y - posS.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.1;
      const attrForce = (dist * dist) / IDEAL_EDGE_LENGTH;
      const fx = (dx / dist) * attrForce;
      const fy = (dy / dist) * attrForce;
      forces[source].x += fx;
      forces[source].y += fy;
      forces[target].x -= fx;
      forces[target].y -= fy;
    });

    // Gravity toward centre
    nodes.forEach((node) => {
      const pos = positions[node.id];
      forces[node.id].x -= pos.x * GRAVITY;
      forces[node.id].y -= pos.y * GRAVITY;
    });

    // Apply forces (clamped by temperature)
    nodes.forEach((node) => {
      const force = forces[node.id];
      const pos = positions[node.id];
      const forceMag = Math.sqrt(force.x * force.x + force.y * force.y) || 0.001;
      const clamp = Math.min(forceMag, temperature);
      positions[node.id] = {
        x: pos.x + (force.x / forceMag) * clamp,
        y: pos.y + (force.y / forceMag) * clamp,
      };
    });

    temperature *= COOLING_FACTOR;
  }

  // Centre the layout at (500, 400) in canvas coords
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  nodes.forEach((node) => {
    const pos = positions[node.id];
    minX = Math.min(minX, pos.x);
    minY = Math.min(minY, pos.y);
    maxX = Math.max(maxX, pos.x);
    maxY = Math.max(maxY, pos.y);
  });
  const offsetX = 500 - (minX + maxX) / 2;
  const offsetY = 400 - (minY + maxY) / 2;

  return nodes.map((node) => ({
    ...node,
    position: {
      x: positions[node.id].x + offsetX,
      y: positions[node.id].y + offsetY,
    },
  }));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Map an ExplorationGraphNode layer to the registered React Flow node type. */
function nodeTypeForLayer(layer: string): string {
  switch (layer) {
    case 'instance':
      return 'conceptInstance';
    case 'ku':
    case 'ko':
    case 'kb':
      return 'sourceNode';
    default:
      return 'explorerConcept';
  }
}

function toRFNodes(nodes: ExplorationGraphNode[]): Node[] {
  return nodes.map((n) => ({
    id: n.node_id,
    type: nodeTypeForLayer(n.layer),
    position: { x: n.position_x ?? 0, y: n.position_y ?? 0 },
    data: { ...n },
  }));
}

/**
 * Hover/click help for a relation between two instances.
 *
 * A `derivation = 'cooccurrence'` edge says the two instances appear in the SAME knowledge unit and
 * that the ontology declares a relationship between their concepts. It does NOT say the document
 * asserts the relationship, so the wording never claims it does, and the stroke is dotted so it
 * cannot be mistaken for the solid, asserted T-Box edges.
 */
export function deducedRelationHint(edge: ExplorationGraphEdge): string {
  const witnesses = Number((edge.metadata as { witness_count?: unknown })?.witness_count ?? 0);
  const unitCount = Number.isFinite(witnesses) && witnesses > 0 ? witnesses : 1;
  return (
    `Deduced relationship, not stated by the source. “${edge.label}” is inferred because both ` +
    `instances appear in the same knowledge unit ` +
    `(${unitCount} unit${unitCount > 1 ? 's' : ''} in this corpus) and the ontology relates their ` +
    `concepts. The direction is the ontology's. Open the instances to read the evidence.`
  );
}

// Exported for the unit test: the dotted stroke of a deduced relation is a correctness property,
// not a decoration (see deducedRelationHint).
export function toRFEdges(edges: ExplorationGraphEdge[]): Edge[] {
  return edges.map((e) => {
    const isHierarchy = e.kind === 'kb_hierarchy';
    const isInstance = e.kind === 'has_instance' || e.kind === 'typed_as';
    const isEvidence = e.kind === 'grounded_in';
    const isDeduced = e.kind === 'instance_relation';

    let stroke = 'var(--cds-border-strong-01, #8d8d8d)';
    if (isInstance) stroke = 'var(--cds-support-teal-60, #007d79)';
    else if (isHierarchy) stroke = 'var(--cds-support-purple-60, #6929c4)';
    else if (isEvidence) stroke = 'var(--cds-support-green-60, #198038)';
    else if (isDeduced) stroke = 'var(--cds-support-caution-major, #ff832b)';

    // Never use `animated: true` — it schedules a CSS animation on every edge
    // SVG path and tanks render performance on large graphs. Use a static
    // strokeDasharray instead to visually distinguish evidence edges.
    //
    // A deduced instance relation is DOTTED ('2 4'), never solid: it is derived from a
    // co-occurrence, so drawing it like the asserted ontology edges would turn a deduction into a
    // claim the source never made.
    const dashArray = isDeduced ? '2 4' : isEvidence ? '5 4' : isHierarchy ? '4 3' : undefined;

    return {
      id: e.edge_id,
      source: e.source_node_id,
      target: e.target_node_id,
      type: 'floating',
      label: e.label,
      animated: false,
      style: { stroke, strokeWidth: isHierarchy ? 1 : 1.5, strokeDasharray: dashArray },
      ...(isDeduced ? { data: { hint: deducedRelationHint(e) } } : {}),
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: stroke,
      },
    };
  });
}

// (status color + failure-reason tooltip now live in <ExplorationStatus />)

// ── Inner canvas (needs to be inside ReactFlowProvider) ───────────────────────

interface CanvasProps {
  graph: ExplorationGraph;
  onNodeClick: NodeMouseHandler;
  pendingLayout: { algorithm: LayoutAlgorithm; spacing: number } | null;
  onLayoutApplied: () => void;
  savedLayout?: SavedLayout;
  onPersistLayout?: (layout: SavedLayout) => void;
}

function ExplorerCanvas({ graph, onNodeClick, pendingLayout, onLayoutApplied, savedLayout, onPersistLayout }: CanvasProps) {
  const rawNodes = useMemo(() => toRFNodes(graph.nodes), [graph.nodes]);
  const rawEdges = useMemo(() => toRFEdges(graph.edges), [graph.edges]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const { fitView } = useReactFlow();
  // Re-fit the viewport after a (re)layout so nodes never end up off-screen or
  // scattered far apart. React Flow's `fitView` prop only fires on first render,
  // so recomputed layouts (initial load, view-mode change, toolbar layout) need
  // an explicit fit. The short delay lets React Flow measure the new node sizes.
  const fitSoon = useCallback(() => {
    window.setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 80);
  }, [fitView]);

  // Track whether we've already applied the layout for this graph snapshot
  const layoutAppliedRef = useRef(false);
  const prevNodeCountRef = useRef(0);

  // Keep a ref to current edges so layout callbacks can read them
  const edgesRef = useRef(edges);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  const nodesRef = useRef(nodes);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  // Same for the persisted layout: the layout effect reacts to the GRAPH, and reads whatever
  // layout is saved at that instant. Depending on the value itself would re-apply the layout
  // every time the exploration query refetches and hands back a new object.
  const savedLayoutRef = useRef(savedLayout);
  useEffect(() => { savedLayoutRef.current = savedLayout; }, [savedLayout]);

  const runLayout = useCallback(
    (algorithm: LayoutAlgorithm, spacing: number, nodesToLayout: Node[], edgesToLayout: Edge[]) => {
      switch (algorithm) {
        case 'circular':
          return applyCircularLayout(nodesToLayout);
        case 'grid':
          return applyGridLayout(nodesToLayout);
        case 'hierarchical':
          return applyHierarchicalLayout(nodesToLayout, edgesToLayout);
        default:
          return applyForceLayout(nodesToLayout, edgesToLayout, spacing);
      }
    },
    [],
  );

  // Initial layout on graph load / view mode change
  useEffect(() => {
    const nodeCount = rawNodes.length;
    if (nodeCount === 0) {
      setNodes([]);
      setEdges([]);
      layoutAppliedRef.current = false;
      prevNodeCountRef.current = 0;
      return;
    }
    if (!layoutAppliedRef.current || nodeCount !== prevNodeCountRef.current) {
      // A4: restore the user's saved manual layout when present; only fall back
      // to the auto force-layout for an exploration that has never been arranged.
      const restored = applySavedLayout(rawNodes, savedLayoutRef.current);
      const layouted = restored.hasSaved ? restored.nodes : applyForceLayout(rawNodes, rawEdges, 50);
      setNodes(layouted);
      setEdges(rawEdges);
      layoutAppliedRef.current = true;
      prevNodeCountRef.current = nodeCount;
      fitSoon();
    } else {
      // Same count — only refresh grounding data (position unchanged)
      setNodes((prev) =>
        prev.map((n) => {
          const fresh = rawNodes.find((r) => r.id === n.id);
          return fresh ? { ...n, data: fresh.data } : n;
        }),
      );
      setEdges(rawEdges);
    }
  }, [rawNodes, rawEdges, setNodes, setEdges, fitSoon]);

  // Apply layout triggered from the toolbar
  useEffect(() => {
    if (!pendingLayout) return;
    setNodes((current) => runLayout(pendingLayout.algorithm, pendingLayout.spacing, current, edgesRef.current));
    onLayoutApplied();
    fitSoon();
  }, [pendingLayout, runLayout, setNodes, onLayoutApplied, fitSoon]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={onNodeClick}
      onNodeDragStop={() => onPersistLayout?.(collectLayout(nodesRef.current))}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      connectionMode={ConnectionMode.Loose}
      nodesDraggable
      nodesConnectable={false}
      elementsSelectable
      fitView
      fitViewOptions={{ padding: 0.15 }}
      className="ke-page__flow"
    >
      <Background />
      <Controls showInteractive={false} />
      <Panel position="top-right">
        <ExplorerLegend />
      </Panel>
      <MiniMap
        nodeColor={(n) => {
          const d = n.data as { grounding_count?: number; layer?: string };
          if (d.layer === 'instance') return '#007d79';
          if (d.layer === 'kb') return '#6929c4';
          if (d.layer === 'ko') return '#1192e8';
          if (d.layer === 'ku') return '#198038';
          return (d.grounding_count ?? 0) > 0 ? '#0f62fe' : '#c6c6c6';
        }}
        pannable
        zoomable
      />
    </ReactFlow>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function KnowledgeExplorerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeLabel, setSelectedNodeLabel] = useState<string | null>(null);
  const [selectedNodeLayer, setSelectedNodeLayer] = useState<string | null>(null);

  // ── View mode ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ExplorerTab>('ontology');
  // The canvas keeps its last graph mode while the Quality tab is open, so switching back neither
  // refetches the graph nor loses the layout the user arranged.
  const viewMode: ExplorationViewMode = isGraphMode(activeTab) ? activeTab : 'ontology';
  const isQualityTab = activeTab === 'quality';
  const [isFacetsOpen, setIsFacetsOpen] = useState(false);

  // ── Layout state (lives here so toolbar can drive it) ──────────────────────
  const [currentAlgorithm, setCurrentAlgorithm] = useState<LayoutAlgorithm>('force');
  const [spacing, setSpacing] = useState(50);
  const [pendingLayout, setPendingLayout] = useState<{ algorithm: LayoutAlgorithm; spacing: number } | null>(null);
  const [isApplyingLayout, setIsApplyingLayout] = useState(false);

  const handleApplyLayout = useCallback(
    (algorithm: LayoutAlgorithm, s = spacing) => {
      setCurrentAlgorithm(algorithm);
      setIsApplyingLayout(true);
      setPendingLayout({ algorithm, spacing: s });
    },
    [spacing],
  );

  const handleLayoutApplied = useCallback(() => {
    setPendingLayout(null);
    setIsApplyingLayout(false);
  }, []);

  // A4: persist the manual node layout (debounced) so a reorganized graph
  // survives a reload. Fire-and-forget; a failed save just means the next drag
  // retries. Cached graph is updated so a remount restores the saved positions.
  const saveLayoutMutation = useMutation({
    mutationFn: (layout: SavedLayout) => explorationApi.saveLayout(id!, layout),
    onSuccess: (_res, layout) => {
      queryClient.setQueryData<ExplorationGraph>(['exploration-graph', id, viewMode], (prev) =>
        prev ? { ...prev, exploration: { ...prev.exploration, node_layout: layout } } : prev,
      );
    },
  });
  const layoutTimer = useRef<number | null>(null);
  const persistLayout = useCallback(
    (layout: SavedLayout) => {
      if (layoutTimer.current) window.clearTimeout(layoutTimer.current);
      layoutTimer.current = window.setTimeout(() => saveLayoutMutation.mutate(layout), 600);
    },
    [saveLayoutMutation],
  );

  // ── Fetch graph ────────────────────────────────────────────────────────────
  const {
    data: graph,
    isLoading,
    isError,
    error,
  } = useQuery<ExplorationGraph>({
    queryKey: ['exploration-graph', id, viewMode],
    queryFn: () => explorationApi.getGraph(id!, viewMode),
    enabled: !!id,
    refetchInterval: (query) => {
      const g = query.state.data as ExplorationGraph | undefined;
      if (!g) return 5_000;
      const status = g.exploration.populate_status;
      return status === 'running' || status === 'pending' ? 3_000 : false;
    },
  });

  // ── Populate trigger ───────────────────────────────────────────────────────
  // Fire-and-forget server side: the route flips the row to `pending` and returns, so a lost
  // response is by far the most likely reading of a timeout here.
  const repopulateMutation = useResilientMutation<{ ok: true }, RepopulateVars>({
    mutationFn: (vars) =>
      explorationApi.triggerPopulate(vars.explorationId).then(() => ({ ok: true as const })),
    verify: verifyPopulateStarted,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exploration-graph', id] });
    },
  });

  const repopulateFailure = outcomeMessage(repopulateMutation.error);

  // ── Node click → evidence panel ───────────────────────────────────────────
  const handleNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    const nodeData = node.data as { label?: string; layer?: string };
    setSelectedNodeId(node.id);
    setSelectedNodeLabel(nodeData.label ?? node.id);
    setSelectedNodeLayer(nodeData.layer ?? 'ontology');
  }, []);

  const handleCloseEvidence = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedNodeLabel(null);
    setSelectedNodeLayer(null);
  }, []);

  // ── View mode change — reset selected node + re-apply layout ──────────────
  const handleViewModeChange = useCallback((mode: ExplorerTab) => {
    setActiveTab(mode);
    setSelectedNodeId(null);
    setSelectedNodeLabel(null);
    setSelectedNodeLayer(null);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  const exploration = graph?.exploration;
  const isPopulating =
    exploration?.populate_status === 'running' ||
    exploration?.populate_status === 'pending';

  return (
    <Content className="app-content app-content--full">
      <div className="ke-page">
        {/* ── Toolbar ── */}
        <div className="ke-page__toolbar">
          {/* Left: back + exploration info */}
          <div className="ke-page__toolbar-left">
            <Button
              kind="ghost"
              size="sm"
              renderIcon={ChevronLeft}
              onClick={() => navigate('/explorer')}
            >
              All explorations
            </Button>

            <div className="ke-page__toolbar-title">
              {exploration ? (
                <>
                  <span className="ke-page__name">{exploration.name}</span>
                  <Tag size="sm" type="outline">
                    {exploration.ontology_name ?? exploration.ontology_id}
                  </Tag>
                  <Tag size="sm" type="outline">
                    v{exploration.ontology_version_number ?? '?'}
                  </Tag>
                  <Tag size="sm" type="outline">
                    KB: {exploration.knowledge_base_name ?? exploration.knowledge_base_id}
                  </Tag>
                  <ExplorationStatus
                    status={exploration.populate_status}
                    error={exploration.populate_error}
                  />
                  {exploration.concepts_grounded != null &&
                    exploration.concepts_total != null && (
                      <Tag size="sm" type="gray">
                        {exploration.concepts_grounded}/{exploration.concepts_total} grounded
                      </Tag>
                    )}
                  {exploration.instances_total != null && exploration.instances_total > 0 && (
                    <Tag size="sm" type="teal">
                      {exploration.instances_total} instances
                    </Tag>
                  )}
                </>
              ) : (
                <InlineLoading description="Loading exploration…" />
              )}
            </div>
          </div>

          {/* Center: view mode + layout controls */}
          <div className="ke-page__toolbar-center">
            {/* View mode switcher */}
            <div className="ke-page__view-mode">
              {VIEW_MODES.map((vm) => {
                const Icon = vm.icon;
                const isActive = activeTab === vm.value;
                return (
                  <button
                    key={vm.value}
                    className={`ke-page__view-btn${isActive ? ' ke-page__view-btn--active' : ''}`}
                    title={vm.tooltip}
                    onClick={() => handleViewModeChange(vm.value)}
                  >
                    <Icon size={14} />
                    <span>{vm.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="ke-page__toolbar-divider" />

            {/* Layout controls */}
            {layoutOptions.map((option) => {
              const Icon = option.icon;
              const isActive = currentAlgorithm === option.value;
              return (
                <IconButton
                  key={option.value}
                  kind={isActive ? 'primary' : 'ghost'}
                  size="sm"
                  label={option.tooltip}
                  align="bottom"
                  onClick={() => handleApplyLayout(option.value, spacing)}
                  disabled={isApplyingLayout}
                >
                  <Icon size={16} />
                </IconButton>
              );
            })}

            <div className="ke-page__toolbar-divider" />

            <div className="ke-page__toolbar-spacing" title="Adjust spacing between nodes when applying a layout">
              <span className="ke-page__toolbar-spacing-label">Spacing</span>
              <input
                type="range"
                min={10}
                max={100}
                step={5}
                value={spacing}
                onChange={(e) => setSpacing(Number(e.target.value))}
                className="ke-page__toolbar-spacing-slider"
                aria-label="Layout spacing"
                title={`Spacing: ${spacing}`}
              />
            </div>

            <div className="ke-page__toolbar-divider" />

            <IconButton
              kind="primary"
              size="sm"
              label={isApplyingLayout ? 'Applying layout…' : 'Re-apply current layout with selected spacing'}
              align="bottom"
              onClick={() => handleApplyLayout(currentAlgorithm, spacing)}
              disabled={isApplyingLayout}
            >
              <Play size={16} />
            </IconButton>
          </div>

          {/* Right: actions */}
          <div className="ke-page__toolbar-actions">
            {isPopulating && <InlineLoading description="Populating…" />}
            <IconButton
              kind="ghost"
              size="sm"
              label="Facet search (Claims / Entities / Semantic) in this knowledge base"
              align="bottom-end"
              onClick={() => setIsFacetsOpen(true)}
              data-testid="explorer-facet-search"
            >
              <Search size={16} />
            </IconButton>
            <IconButton
              kind="ghost"
              size="sm"
              label="Re-run populate"
              align="bottom-end"
              disabled={isPopulating || repopulateMutation.isPending}
              onClick={() =>
                void repopulateMutation.submit({
                  explorationId: id!,
                  startedAtBefore: exploration?.populate_started_at ?? null,
                  statusBefore: exploration?.populate_status ?? null,
                })
              }
            >
              <Renew size={16} />
            </IconButton>
          </div>
        </div>

        {/* Facet Search in-context (A11): scoped to this exploration's KB. */}
        {exploration?.knowledge_base_id && (
          <Modal
            open={isFacetsOpen}
            modalHeading={`Facet search — ${exploration.knowledge_base_name ?? 'knowledge base'}`}
            passiveModal
            size="lg"
            onRequestClose={() => setIsFacetsOpen(false)}
          >
            <FacetSearchPanel kbId={exploration.knowledge_base_id} />
          </Modal>
        )}

        {/* Re-run outcome. `isVerifying` gets its own wording because the honest thing to say
            during that window is that we are checking, not that we are populating. */}
        {(repopulateFailure || repopulateMutation.isVerifying) && (
          <ActionableNotification
            kind={repopulateMutation.isVerifying || repopulateMutation.isUnconfirmed ? 'warning' : 'error'}
            title={
              repopulateMutation.isVerifying
                ? 'Checking'
                : repopulateMutation.isUnconfirmed
                  ? 'Not confirmed'
                  : 'Re-run failed'
            }
            subtitle={
              repopulateMutation.isVerifying
                ? 'Connection problem. Checking with the server whether the re-run started…'
                : (repopulateFailure ?? '')
            }
            lowContrast
            hideCloseButton={repopulateMutation.isVerifying}
            inline={false}
            actionButtonLabel="Retry"
            onActionButtonClick={() => void repopulateMutation.retry()}
            onCloseButtonClick={() => repopulateMutation.reset()}
          />
        )}

        {/* ── Main area ── */}
        <div className="ke-page__canvas-wrapper">
          {/* The Quality tab replaces the canvas rather than overlaying it: the KPI list is a
              reading surface, and half a graph behind it would only compete for attention. */}
          {isQualityTab && id && <GraphQualityPanel explorationId={id} />}

          {!isQualityTab && isLoading && (
            <div className="ke-page__centered">
              <InlineLoading description="Loading graph…" />
            </div>
          )}

          {!isQualityTab && isError && (
            <div className="ke-page__centered">
              <InlineNotification
                kind="error"
                title="Failed to load exploration"
                subtitle={(error as Error)?.message}
                hideCloseButton
              />
            </div>
          )}

          {!isQualityTab && !isLoading && !isError && graph && (
            <ReactFlowProvider>
              <ExplorerCanvas
                graph={graph}
                onNodeClick={handleNodeClick}
                pendingLayout={pendingLayout}
                onLayoutApplied={handleLayoutApplied}
                savedLayout={graph.exploration?.node_layout}
                onPersistLayout={persistLayout}
              />
            </ReactFlowProvider>
          )}

          {/* Evidence panel (slide-in, overlays the canvas) */}
          {!isQualityTab && id && (
            <EvidencePanel
              explorationId={id}
              nodeId={selectedNodeId}
              nodeLabel={selectedNodeLabel}
              nodeLayer={selectedNodeLayer}
              onClose={handleCloseEvidence}
            />
          )}
        </div>
      </div>
    </Content>
  );
}

// Made with Bob
